import { Loading } from '@/components/shared';
import { type EntityMention, useEntityMentions } from '@/hooks/useEntities';

export function EntityMentionsList({
  matterGuid,
  entityId,
  documentNames,
  onOpen,
}: {
  matterGuid: string;
  entityId: string;
  documentNames: ReadonlyMap<string, string>;
  onOpen: (documentGuid: string, segmentIndex?: number) => void;
}) {
  const { mentions, truncated, isLoading, isError, mutate } = useEntityMentions(matterGuid, entityId);

  if (isLoading) {
    return (
      <div className="flex h-full items-center justify-center">
        <Loading />
      </div>
    );
  }

  if (isError) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-2 px-4 text-center">
        <p className="text-sm text-gray-500">{"Couldn't load mentions."}</p>
        <button
          type="button"
          onClick={() => { void mutate(); }}
          className="text-sm font-medium text-gray-700 hover:text-gray-900"
        >
          Retry
        </button>
      </div>
    );
  }

  if (mentions.length === 0) {
    return (
      <p className="px-4 py-8 text-center text-sm text-gray-500">No passages found.</p>
    );
  }

  return (
    <div className="flex h-full min-h-0 flex-col">
      <ul className="min-h-0 flex-1 overflow-y-auto divide-y divide-gray-100">
        {mentions.map(mention => (
          <li key={mention.id}>
            <MentionRow
              mention={mention}
              documentName={documentNames.get(mention.document_guid) || mention.document_name}
              onOpen={onOpen}
            />
          </li>
        ))}
      </ul>
      {truncated ? (
        <p className="shrink-0 border-t border-gray-100 px-4 py-2 text-[11px] text-gray-400">
          Showing the first {mentions.length} passages.
        </p>
      ) : null}
    </div>
  );
}

function MentionRow({
  mention,
  documentName,
  onOpen,
}: {
  mention: EntityMention;
  documentName: string;
  onOpen: (documentGuid: string, segmentIndex?: number) => void;
}) {
  return (
    <button
      type="button"
      onClick={() => onOpen(
        mention.document_guid,
        mention.segment_index === null ? undefined : mention.segment_index,
      )}
      className="w-full px-4 py-3 text-left hover:bg-gray-50"
    >
      <span className="block truncate text-sm font-medium text-gray-900">{mention.name}</span>
      <span className="mt-0.5 block truncate text-xs text-gray-500">{documentName}</span>
      {mention.passage ? (
        <span className="mt-1 line-clamp-3 block text-[13px] leading-5 text-gray-600">{mention.passage}</span>
      ) : null}
    </button>
  );
}
